import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Resets scroll to the top on every route change.
 * Hash navigation (e.g. /#pricing) is handled by the destination page.
 *
 * Mobile Safari / Chrome sometimes restore the previous scroll position
 * asynchronously after we run, so we:
 *  - disable browser scroll restoration
 *  - reset every plausible scroller (window, documentElement, body,
 *    #page-scroll) with behavior:'instant' (falls back to 'auto')
 *  - re-run on the next animation frame + a short timeout to defeat
 *    late scroll restoration.
 */
const ScrollToTop = () => {
  const { pathname, hash, key } = useLocation();

  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    if (hash) return;

    const reset = () => {
      const opts: ScrollToOptions = { top: 0, left: 0, behavior: "instant" as ScrollBehavior };
      try { window.scrollTo(opts); } catch { window.scrollTo(0, 0); }
      if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
      if (document.documentElement) document.documentElement.scrollTop = 0;
      if (document.body) document.body.scrollTop = 0;
      const container = document.getElementById("page-scroll");
      if (container) container.scrollTop = 0;
    };

    reset();
    const raf = requestAnimationFrame(reset);
    const t = setTimeout(reset, 60);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [pathname, hash, key]);

  return null;
};

export default ScrollToTop;
